import React, { useEffect, useRef, useState } from "react";
import "./projectReadinessForm.scss";
import Header from "../../../layout/header/Header";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../../httpInterceptor";
import { IoArrowBackSharp } from "react-icons/io5";

interface ProjectReadinessFormProps {
  embedded?: boolean;
}

const ProjectReadinessForm: React.FC<ProjectReadinessFormProps> = ({ embedded = false }) => {
  const navigate = useNavigate();

  // Prevent vertical scrolling on body and container for this page only
  useEffect(() => {
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    // Also hide overflow on the .container class from App.scss
    const containerElement = document.querySelector('.container') as HTMLElement;
    const originalContainerOverflow = containerElement?.style.overflow;
    if (containerElement) {
      containerElement.style.overflow = 'hidden';
    }
    
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      if (containerElement) {
        containerElement.style.overflow = originalContainerOverflow || '';
      }
    };
  }, []);

  // State for form datac
  const [formData, setFormData] = useState({
    projectName: "",
    du: "",
    accountName: "",
    projectDescription: "",
    assessorName: "",
    assessmentType: "",
  });

  // State for loading and questions
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questions, setQuestions] = useState<any[]>([]);
  
  // State for DU options
  const [duOptions, setDuOptions] = useState<string[]>([]);
  const [duLoading, setDuLoading] = useState(false);
  
  // State for assessor name autocomplete
  const [assessorSuggestions, setAssessorSuggestions] = useState<any>(null);
  const [assessorNames, setAssessorNames] = useState<any>(null);
  const [isInvalidAssessor, setIsInvalidAssessor] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const assessorInputRef = useRef<HTMLInputElement>(null);

  // State for custom DU dropdown
  const [duDropdownOpen, setDuDropdownOpen] = useState(false);
  const duDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch assessor data from API
  const fetchUserDataForAssessor = async (query: string) => {
    if (!query) {
      setAssessorSuggestions(null);
      setIsInvalidAssessor(true);
      return;
    }
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_USER_SERVICE_BASE_URL}/getPSNumber/?search=${query}`
      );

      const result = res.data.filter((item: any) => {
        if (!query) {
          setAssessorSuggestions(null);
          setIsInvalidAssessor(true);
        }
        {
          const searchText = query.toLowerCase();
          const fullName = item.name.toLowerCase();

          return query && fullName.startsWith(searchText);
        }
      });
      setAssessorSuggestions(result);
      setIsInvalidAssessor(result.length === 0);
      const names = result.map((item: any) => {
        const { name, ...rest } = item;
        return name;
      });
      setAssessorNames(names);
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch DU details from API
  useEffect(() => {
    const fetchDuDetails = async () => {
      setDuLoading(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_DU_SERVICE_BASE_URL}/dudetails`
        );
        if (response.data && response.data.duNames) {
          setDuOptions(response.data.duNames);
        }
      } catch (error) {
        console.error("Error fetching DU details:", error);
        toast.error("Error fetching DU options");
      } finally {
        setDuLoading(false);
      }
    };
    fetchDuDetails();
  }, []);

  // Fetch questions for Assessment Type dropdown
  useEffect(() => {
    const fetchQuestions = async () => {
      setQuestionsLoading(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_READINESS_QUESTION_SERVICE_BASE_URL}/question-readiness`
        );
        setQuestions(response.data);
      } catch (error) {
        console.error("Error fetching questions:", error);
        toast.error("Error fetching assessment types");
      } finally {
        setQuestionsLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  // Group questions by set to get unique assessment types
  const groupQuestionsBySet = (questions: any) => {
    return questions.reduce((acc: any, question: any) => {
      const rawSet = question.set || question.setName || question.set_name || "";
      const set = rawSet ? String(rawSet).trim() : ""; // Trim to remove leading/trailing spaces
      if (set && !acc[set]) acc[set] = [];
      if (set) acc[set].push(question);
      return acc;
    }, {});
  };

  const groupedQuestionsBySet = groupQuestionsBySet(questions);
  const assessmentTypes = Object.keys(groupedQuestionsBySet);

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle assessor name input changes
  const handleAssessorChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setIsInvalidAssessor(false);

    if (value.trim() === "") {
      setAssessorSuggestions([]);
      setIsInvalidAssessor(false);
    } else {
      fetchUserDataForAssessor(value);
    }
  };

  // Handle assessor suggestion click
  const handleAssessorOnClick = (value: string) => {
    setFormData((prevData: any) => ({
      ...prevData,
      assessorName: value,
    }));
    setAssessorSuggestions(null);
    setIsInvalidAssessor(false);
    setHasBlurred(false);
  };

  // Handle assessor input blur
  const handleAssessorBlur = () => {
    setTimeout(() => {
      setAssessorSuggestions(null);
      setIsInvalidAssessor(false);
      setHasBlurred(true);
    }, 300);
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      formData.projectName.trim() !== "" &&
      formData.du.trim() !== "" &&
      formData.accountName.trim() !== "" &&
      formData.projectDescription.trim() !== "" &&
      formData.assessorName.trim() !== "" &&
      formData.assessmentType.trim() !== ""
    );
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!assessorNames || !assessorNames.includes(formData.assessorName)) {
      toast.error("Please select Assessor from suggestions!");
      return;
    }
    
    if (!isFormValid()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // Prepare payload for preproject readiness API
      // Trim all fields to prevent leading/trailing spaces
      const payload = {
        assessorName: formData.assessorName.trim(),
        setName: formData.assessmentType.trim(),
        accountName: formData.accountName.trim(),
        projectDescription: formData.projectDescription.trim(),
        duName: formData.du.trim(),
        projectName: formData.projectName.trim(),
      };

      // POST to preproject readiness service API
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_PRE_PROJECT_READINESS_SERVICE_BASE_URL}/preproject`,
        payload
      );

      toast.success("Project Readiness Assessment created successfully");
      // If API returned the created preproject record, navigate to the readiness assessment
      const preprojectId = response?.data?.preprojectId || response?.data?._id || response?.data?.assessmentId;
      const returnedSetName = response?.data?.setName || response?.data?.set_name || payload.setName;
      if (preprojectId && returnedSetName) {
        navigate(
          `/dashboard/re-assessment/${preprojectId}/${encodeURIComponent(returnedSetName)}`,
          { state: { fromProjectReadiness: true, readinessData: response.data } }
        );
      } else {
        // Fallback to dashboard view if API didn't return identifiers
        navigate("/dashboard", { state: { showProjectReadiness: true } });
      }
    } catch (error: any) {
      console.error("Error creating readiness assessment:", error);
      toast.error(
        error.response?.data?.message || 
        "Failed to create readiness assessment. Please try again."
      );
    }
  };

  // Navigate back to dashboard
  const handleBack = () => {
    navigate("/dashboard", { state: { showProjectReadiness: true } });
  };

  return (
    <div className="projectReadinessContainer">
      {!embedded && <Header />}
      <form onSubmit={handleSubmit} className="createProjectForm">
        <div className="createProjectFormBox">
          <div className="createProjectFormRow">
            <div className="row custom-dropdown-container">
              <label>
                DU
                <span style={{ color: "red" }}>*</span>
              </label>
              <div
                ref={duDropdownRef}
                className={`custom-dropdown ${duDropdownOpen ? 'open' : ''} ${duLoading ? 'disabled' : ''}`}
                onClick={() => !duLoading && setDuDropdownOpen(!duDropdownOpen)}
                onBlur={() => setTimeout(() => setDuDropdownOpen(false), 200)}
                tabIndex={0}
              >
                <div className="custom-dropdown-selected">
                  {formData.du || (duLoading ? "Loading DU options..." : "Select DU")}
                </div>
                <div className="custom-dropdown-arrow"></div>
                {duDropdownOpen && duDropdownRef.current && (() => {
                  const rect = duDropdownRef.current!.getBoundingClientRect();
                  return (
                    <ul
                      className="custom-dropdown-list"
                      style={{
                        position: 'fixed',
                        top: rect.bottom,
                        left: rect.left,
                        width: rect.width,
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {duOptions.map((du) => (
                        <li
                          key={du}
                          className={formData.du === du ? 'selected' : ''}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData({ ...formData, du });
                            setDuDropdownOpen(false);
                          }}
                        >
                          {du}
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              </div>
            </div>
            <div className="row">
              <label>
                Project Name
                <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                placeholder="Enter project name"
                autoComplete="off"
                required
              />
            </div>
            <div className="row">
              <label>
                Account
                <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="accountName"
                value={formData.accountName}
                onChange={handleChange}
                placeholder="Enter account name"
                autoComplete="off"
                required
              />
            </div>
          </div>
          
          <div className="createProjectFormRow">
            <div className="row projectIdInputBoxWithSuggestions">
              <label>
                Assessor (By Name)
                <span style={{ color: "red" }}>*</span>
              </label>
              <input
                ref={assessorInputRef}
                type="text"
                name="assessorName"
                value={formData.assessorName}
                onChange={handleAssessorChange}
                onFocus={(e) => fetchUserDataForAssessor(e.target.value)}
                onBlur={handleAssessorBlur}
                placeholder="Enter assessor name"
                autoComplete="off"
                required
              />
              {assessorSuggestions?.length > 0 && assessorInputRef.current && (() => {
                const rect = assessorInputRef.current!.getBoundingClientRect();
                return (
                  <ul
                    className="showProjResults"
                    style={{
                      position: 'fixed',
                      top: rect.bottom,
                      left: rect.left,
                      width: rect.width,
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {assessorSuggestions.map((result: any, idx: number) => (
                      <li
                        className="resultItems"
                        key={idx}
                        onClick={() => handleAssessorOnClick(result.name)}
                      >
                        <span>{result.name}</span>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
            <div className="row">
              <label>
                Assessment Type
                <span style={{ color: "red" }}>*</span>
              </label>
              <select
                name="assessmentType"
                value={formData.assessmentType}
                onChange={handleChange}
                required
                disabled={questionsLoading}
              >
                <option value="">
                  {questionsLoading
                    ? "Loading assessment types..."
                    : "Select Assessment Type"}
                </option>
                {assessmentTypes.length > 0 ? (
                  assessmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))
                ) : !questionsLoading && questions.length > 0 ? (
                  <option disabled>No assessment types found</option>
                ) : null}
              </select>
            </div>
            <div className="row full-width">
              <label>
                Project Description
                <span style={{ color: "red" }}>*</span>
              </label>
              <textarea
                name="projectDescription"
                value={formData.projectDescription}
                onChange={handleChange}
                placeholder="Enter project description"
                required
                style={{
                  width: "100%",
                  minHeight: "42px",
                  padding: "8px",
                  resize: "vertical"
                }}
              />
            </div>
          </div>
        </div>

        <div className="createProjectBtn">
          <button
            type="submit"
            disabled={!isFormValid()}
            style={{ cursor: !isFormValid() ? "not-allowed" : "pointer" }}
          >
            Submit
          </button>
        </div>
      </form>
      <div className="backToDashboard">
        <button onClick={handleBack}>
          <IoArrowBackSharp
            style={{ translate: "-10px", fontSize: "1.2rem" }}
          />
          <span style={{ marginLeft: "5px" }}>Back</span>
        </button>
      </div>
    </div>
  );
};

export default ProjectReadinessForm;
